import { useCallback, useEffect, useRef, useState } from 'react';

import {
  LoyaltyOstType,
  LoyaltyPromotionType,
  useCreateOrderSelectionByMultipleTypeMutation,
} from 'generated/graphql-gateway';
import { useSyncStorageObject } from 'hooks/use-local-storage-state/use-sync-storage-state';
import { useLoyaltyIncentivesEvaluation } from 'state/loyalty/hooks/use-loyalty-incentive-evaluation';
import { SyncStorageKey } from 'utils/environment/sync-storage';
import logger from 'utils/logger';

import { IGetNewShortCodeParams, IShortCodeData, IUserShortCode, ShortCodeState } from './types';
import { differenceMsFromNow, isExpired } from './utils';

/**
 * useShortCode holds otp short-code and its state.
 */
export const useShortCode = (): IUserShortCode => {
  const [generateOrderSelection] = useCreateOrderSelectionByMultipleTypeMutation({
    onCompleted: ({ loyaltyCreateOrderSelectionByMultipleType }) => {
      const otpCodeData = loyaltyCreateOrderSelectionByMultipleType.find(
        el => el.ostType === LoyaltyOstType.OTP
      );

      if (!otpCodeData) {
        return;
      }
      const { key: newShortCode, expirationTime } = otpCodeData;

      setShortCodeData({
        shortCode: newShortCode,
        expiry: expirationTime,
      });
      setShortCodeState(ShortCodeState.Pending);
    },
    onError: () => {
      setRequestError();
    },
  });
  const { evaluateLoyaltyIncentives } = useLoyaltyIncentivesEvaluation();
  const generateCodeOtpTimeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const abortControllerRef = useRef<AbortController>(undefined);
  const [shortCodeLoading, setShortCodeLoading] = useState<boolean>(false);
  const [shortCodeStorageItem, setShortCodeData, clearShortCodeData] = useSyncStorageObject<
    IShortCodeData
  >(SyncStorageKey.IN_RESTAURANT_REDEMPTION_SHORT_CODE, {});
  const { shortCode, expiry } = shortCodeStorageItem || {};
  const [shortCodeState, setShortCodeState] = useState<ShortCodeState>(() =>
    shortCode ? ShortCodeState.Pending : ShortCodeState.None
  );
  const shouldEvaluateIncentivesRef = useRef<boolean>(undefined);
  // evaluate incentives only if the short code is expired to avoid unnecessary requests
  shouldEvaluateIncentivesRef.current = shortCodeState === ShortCodeState.Expired;

  const setRequestError = useCallback(() => {
    clearShortCodeData();
    setShortCodeState(ShortCodeState.RequestError);
  }, [clearShortCodeData]);

  const setShortCodeClaimed = useCallback(() => {
    setShortCodeState(ShortCodeState.Claimed);
  }, []);

  const setShortCodeExpired = useCallback(() => {
    setShortCodeState(ShortCodeState.Expired);
  }, []);

  const resetShortCode = useCallback(() => {
    clearShortCodeData();
    setShortCodeState(ShortCodeState.None);
    setShortCodeLoading(false);
  }, [clearShortCodeData]);

  const setIncentiveValidationError = useCallback(() => {
    setShortCodeState(ShortCodeState.OfferValidationError);
  }, [setShortCodeState]);

  /**
   * getNewShortCode works as a debounce function in order to prevent running
   * multiple mutations at the same time
   */
  const getNewShortCode = useCallback(
    ({
      inRestaurantOrder,
      loyaltyId,
      restaurantId,
      serviceMode,
      appliedOffers,
    }: IGetNewShortCodeParams) => {
      if (generateCodeOtpTimeoutRef.current) {
        clearTimeout(generateCodeOtpTimeoutRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      setShortCodeLoading(true);
      generateCodeOtpTimeoutRef.current = global.setTimeout(async () => {
        const controller = window.AbortController ? new window.AbortController() : undefined;
        abortControllerRef.current = controller;

        // flag to avoid set the state if the request is aborted
        let isRequestAborted = false;
        if (controller?.signal) {
          controller.signal.onabort = () => {
            isRequestAborted = true;
          };
        }

        try {
          // Incentive validations
          let hasValidationErrors = false;
          if (shouldEvaluateIncentivesRef.current && appliedOffers.length) {
            const evaluationResult = await evaluateLoyaltyIncentives({
              appliedIncentives: appliedOffers,
              incentiveType: LoyaltyPromotionType.OFFER,
              loyaltyId,
              storeId: restaurantId,
              serviceMode,
            });

            // skip validation if the request was aborted to prevent set state
            if (!isRequestAborted && evaluationResult) {
              hasValidationErrors = appliedOffers.some(({ id }: any) => !!evaluationResult[id]);

              if (hasValidationErrors) {
                setIncentiveValidationError();
              }
            }
          }

          if (!hasValidationErrors) {
            await generateOrderSelection({
              variables: {
                inRestaurantOrder,
                restaurantId,
                loyaltyId,
                ostTypes: [LoyaltyOstType.LOYALTY_ID, LoyaltyOstType.OTP],
              },
              context: {
                fetchOptions: {
                  signal: controller?.signal,
                },
              },
            });
          }
        } catch (e) {
          logger.error(`Error generating a short code: ${e}`);
        } finally {
          generateCodeOtpTimeoutRef.current = undefined;
          setShortCodeLoading(false);
        }
      }, 250);
    },
    [evaluateLoyaltyIncentives, generateOrderSelection, setIncentiveValidationError]
  );

  useEffect(() => {
    if (!expiry) {
      return;
    } else if (isExpired(expiry)) {
      setShortCodeExpired();
      return;
    }

    const codeDurationMilliseconds = differenceMsFromNow(expiry);
    const timeoutId = setTimeout(setShortCodeExpired, codeDurationMilliseconds);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [expiry, setShortCodeExpired]);

  return {
    resetShortCode,
    getNewShortCode,
    setShortCodeClaimed,
    setShortCodeExpired,
    shortCode,
    shortCodeLoading,
    shortCodeState,
  };
};
